/* Which language to read the app in — STORY-042.
 *
 * `i18n.md` states the order: explicit user preference → account setting →
 * `Accept-Language` → geo hint → source locale. The web client implemented only
 * the third rung, so the browser always won, on every screen, for everyone —
 * while emails rendered in the recipient's stored locale. A Turkish Member on a
 * borrowed English laptop got Turkish mail and an English screen, which reads
 * as a bug in the translations rather than as the missing feature it was.
 *
 * ── the browser store is for people who are not signed in ───────────────────
 * A signed-out visitor's choice has nowhere else to live, and losing it at the
 * sign-in screen means choosing again on the screen you chose it from. It is
 * adopted as the account's locale on first sign-in rather than being
 * overwritten by whatever the browser says.
 *
 * ── `localStorage` is legitimate here, and that is worth stating ────────────
 * `security.md` forbids a session token in it. A language preference is neither
 * a credential nor personal data — it is the same class of thing as a theme,
 * and the alternative is a cookie sent on every request for no reason.
 */

import { LOCALES, type Locale, SOURCE_LOCALE } from '@eventuley/i18n';

const KEY = 'eventuley.locale';

/** Whether a stored or supplied tag is one the product actually ships. */
export const isSupportedLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

/**
 * The visitor's own choice, if they made one before signing in.
 *
 * Anything unreadable — a cleared store, a private-mode refusal, a value from an
 * older build — falls through to the rest of the chain rather than throwing. A
 * preference nobody can parse is not worth a broken screen.
 */
export function storedLocale(): Locale | undefined {
  try {
    const value = globalThis.localStorage?.getItem(KEY);
    return isSupportedLocale(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function rememberLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(KEY, locale);
  } catch {
    /* Private mode, or a full store. The choice still applies for this session
     * — it simply will not survive a reload, which is a smaller failure than
     * refusing to change the language at all. */
  }
}

export function forgetLocale(): void {
  try {
    globalThis.localStorage?.removeItem(KEY);
  } catch {
    /* Nothing to do. See above. */
  }
}

/**
 * The chain, in `i18n.md`'s order.
 *
 * @param preferred the reader's own answer, if they have given one: their
 *   choice in this browser, and otherwise their account's stored locale.
 *   Deliberately typed loosely, because a value that is no longer a supported
 *   target must fall THROUGH rather than strand somebody on a broken screen.
 * @param browser `navigator.languages`.
 *
 * The store is not read here. The provider holds the choice in state — a write
 * to `localStorage` renders nothing — and reading it again in here would be a
 * second answer to one question, from a place this function cannot see change.
 */
export function resolvePreferredLocale(
  preferred: string | undefined,
  browser: readonly string[],
): Locale {
  /* A person who has said what they want has said it once, and it wins over
   * whatever the machine they are sitting at happens to be configured for. */
  if (isSupportedLocale(preferred)) return preferred;

  for (const language of browser) {
    const base = language.split('-')[0];
    if (isSupportedLocale(base)) return base;
  }

  return SOURCE_LOCALE;
}

/**
 * How each language names ITSELF.
 *
 * Endonyms, because the control is the one label most likely to be read by
 * somebody who cannot read the current language. "Turkish" is no help to a
 * reader looking at an English screen they do not understand; "Türkçe" is.
 *
 * A literal map rather than `Intl.DisplayNames` in the locale's own tag: two
 * languages is not worth constructing a formatter per option, and the names are
 * fixed data that no catalogue should hold — translating "Türkçe" into English
 * would defeat the point of it.
 */
export const LOCALE_ENDONYMS = {
  en: 'English',
  tr: 'Türkçe',
} as const satisfies Record<Locale, string>;
